"use client";

import { useMemo, useEffect } from "react";
import { FEATURE_FLAGS } from "@cede/types";
import { BRAND_THEME, type BrandTheme } from "@/constants/theme";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { useThemeStore } from "@/store/themeStore";

/**
 * Hook that signals exchange-specific theme intent to the global theme store.
 * Computes the theme from campaign exchangeIds and writes to the campaignTheme slot.
 * DOM class management is handled centrally by AppShell.
 *
 * Applies a branded theme if at least one of the campaign's exchanges has a
 * dedicated theme AND the corresponding exchange page feature flag is enabled.
 * The first matching themed exchange wins.
 *
 * Supported exchanges:
 * - cryptocom: Crypto.com blue theme (#002D74, #129DFF) — always enabled (no page flag)
 * - kraken: Kraken purple theme (#5841d8, #7434f3) — gated by KRAKEN_PAGE_HUB_ENABLE
 * - coinbase: Coinbase dark theme (#0251FF, #141519) — gated by COINBASE_PAGE_HUB_ENABLE
 * - lbank: LBank theme — gated by LBANK_PAGE_HUB_ENABLE
 */
export function useExchangeTheme(exchangeIds: string[] | undefined) {
  const setCampaignTheme = useThemeStore((s) => s.setCampaignTheme);
  const isKrakenPageEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.KRAKEN_PAGE_HUB_ENABLE);
  const isCoinbasePageEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.COINBASE_PAGE_HUB_ENABLE);
  const isLbankPageEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.LBANK_PAGE_HUB_ENABLE);

  // Derive stable theme — find the first exchange that has a branded theme with its flag enabled
  const sortedIds = exchangeIds?.slice().sort().join(",");
  const theme = useMemo<BrandTheme>(() => {
    if (!exchangeIds?.length) return BRAND_THEME.DEFAULT;

    const exchangeThemeMap: Record<string, BrandTheme> = {
      cryptocom: BRAND_THEME.CRYPTOCOM,
      ...(isKrakenPageEnabled && { kraken: BRAND_THEME.KRAKEN }),
      ...(isCoinbasePageEnabled && { coinbase: BRAND_THEME.COINBASE }),
      ...(isLbankPageEnabled && { lbank: BRAND_THEME.LBANK }),
    };

    for (const id of exchangeIds) {
      const mapped = exchangeThemeMap[id];
      if (mapped) return mapped;
    }
    return BRAND_THEME.DEFAULT;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedIds, isKrakenPageEnabled, isCoinbasePageEnabled, isLbankPageEnabled]);

  useEffect(() => {
    setCampaignTheme(theme);
    return () => setCampaignTheme(BRAND_THEME.DEFAULT);
  }, [theme, setCampaignTheme]);
}
